import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import type { FakBiblioEntree, Pole } from '../types';
import { PoleChips } from '../components/annuaire/PoleChips';
import { JOURS_SEMAINE, fakBadgeClass, formatHoraire } from '../components/annuaire/format';
import { LIBELLES_STATUT_FAK, formatRelatif } from '../components/demande/format';
import { FicheFakOverlay, thematiquesDepuisContenu } from '../components/fak/FicheFakOverlay';
import { iso } from '../components/demande/calendrier';

// Bibliothèque FRA — le « chez-soi » des fiches relais d'atelier (BPMN First Aid
// Kit Ateliers). Distinct du catalogue Ateliers (gestion du référentiel) :
// ici on cherche, on découvre, on entretient les fiches de transmission.
// Vos fiches à compléter d'abord, puis l'exploration ; la fiche s'ouvre en
// aperçu par-dessus, l'édition reste dans le panneau de l'atelier.
export function FakPage() {
  const { peutGerer } = useRole();
  const [entrees, setEntrees] = useState<FakBiblioEntree[]>([]);
  const [poles, setPoles] = useState<Pole[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [poleId, setPoleId] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [aSoignerSeuls, setASoignerSeuls] = useState(false);
  const [ficheOuverte, setFicheOuverte] = useState<number | null>(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState<number | null>(null);

  // « Mise à jour ? → Non » (BPMN) : rien à changer, on enregistre la revue
  // et on reprogramme la suivante — la fiche redevient « Publiée » sans faux
  // passage par l'éditeur.
  const confirmerAJour = async (atelierId: number) => {
    setConfirmationEnCours(atelierId);
    setErreur(null);
    try {
      const maj = await api.confirmerRevueFak(atelierId);
      setEntrees((prev) => prev.map((e) => e.atelier_id === atelierId
        ? { ...e, statut: maj.statut, prochaine_revue: maj.prochaine_revue, updated_at: maj.updated_at }
        : e));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Échec de la confirmation');
    } finally {
      setConfirmationEnCours(null);
    }
  };

  useEffect(() => {
    let annule = false;
    (async () => {
      setLoading(true);
      setErreur(null);
      try {
        const [biblio, meta] = await Promise.all([api.getFakBibliotheque(), api.getMeta()]);
        if (annule) return;
        setEntrees(biblio);
        setPoles(meta.poles);
      } catch (err) {
        if (!annule) setErreur(err instanceof Error ? err.message : 'Échec du chargement');
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => { annule = true; };
  }, []);

  // Date locale (pas UTC — toISOString() décale d'un jour en soirée pour les
  // fuseaux à l'ouest de Zurich), même helper que le reste du client.
  const aujourdhuiIso = useMemo(() => iso(new Date()), []);
  // Une fiche « à compléter » : pas encore publiée, repassée en révision, ou
  // publiée mais avec des retours non intégrés / une revue dépassée.
  const aSoigner = (e: FakBiblioEntree) =>
    e.statut === 'manquant' || e.statut === 'brouillon' || e.statut === 'a_reviser'
    || e.nb_retours_non_integres > 0
    || (e.prochaine_revue != null && e.prochaine_revue.slice(0, 10) < aujourdhuiIso);

  const qNorm = q.trim().toLowerCase();
  const filtrees = entrees
    .filter((e) => poleId == null || e.pole_id === poleId)
    .filter((e) => !aSoignerSeuls || aSoigner(e))
    .filter((e) => !qNorm || [e.atelier_nom, e.lieu, e.titulaire_nom, e.contenu, e.echelle_difficulte]
      .some((v) => v?.toLowerCase().includes(qNorm)));

  const mesFichesASoigner = filtrees.filter((e) => e.ma_fiche && aSoigner(e));
  const explorer = filtrees.filter((e) => !mesFichesASoigner.includes(e));

  // Couverture par pôle (gestion) : fiches publiées / ateliers, avant filtres
  // — c'est un indicateur global, pas le reflet de la recherche en cours.
  const couverture = useMemo(() => {
    if (!peutGerer) return [];
    const parPole = new Map<number, { nom: string; couleur: string | null; publiees: number; total: number }>();
    for (const e of entrees) {
      if (e.pole_id == null) continue;
      const c = parPole.get(e.pole_id) ?? { nom: e.pole_nom ?? '—', couleur: e.pole_couleur, publiees: 0, total: 0 };
      c.total += 1;
      if (e.statut === 'publie') c.publiees += 1;
      parPole.set(e.pole_id, c);
    }
    return [...parPole.values()].sort((a, b) => a.nom.localeCompare(b.nom));
  }, [entrees, peutGerer]);

  const nbPubliees = entrees.filter((e) => e.statut === 'publie').length;
  const nbAReviser = entrees.filter((e) => e.statut === 'a_reviser').length;
  const nbManquantes = entrees.filter((e) => e.statut === 'manquant').length;

  const sousTitreCarte = (e: FakBiblioEntree) => [
    e.lieu,
    e.jour_semaine != null ? `${JOURS_SEMAINE[e.jour_semaine]} ${formatHoraire(e.heure_debut, e.heure_fin)}`.trim() : null,
    e.titulaire_nom,
  ].filter(Boolean).join(' · ');

  const carte = (e: FakBiblioEntree) => {
    const themes = thematiquesDepuisContenu(e.contenu);
    const manquant = e.statut === 'manquant' && !e.contenu;
    return (
      <div
        key={e.atelier_id}
        className={`fak-card ${manquant ? 'fak-card-manquant' : ''}`}
        style={{ ['--pole' as string]: e.pole_couleur ?? 'var(--line-strong)' }}
        role="button" tabIndex={0}
        onClick={() => setFicheOuverte(e.atelier_id)}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setFicheOuverte(e.atelier_id); } }}
      >
        <div className="fak-card-nom">{e.atelier_nom}</div>
        <div className="fak-card-sous">{sousTitreCarte(e)}</div>
        {manquant ? (
          <div className="fak-card-foot">
            <span className={fakBadgeClass('manquant')}>{LIBELLES_STATUT_FAK.manquant}</span>
            <span className="text-xs text-ink-3" style={{ fontStyle: 'italic' }}>pas encore de fiche pour cet atelier</span>
          </div>
        ) : (
          <>
            {themes.length > 0 && (
              <div className="fak-card-themes">
                {themes.slice(0, 3).map((t, idx) => <span key={idx} className="fak-theme">{t}</span>)}
                {themes.length > 3 && <span className="fak-theme">+{themes.length - 3}</span>}
              </div>
            )}
            <div className="fak-card-foot">
              {e.statut !== 'publie' && <span className={fakBadgeClass(e.statut)}>{LIBELLES_STATUT_FAK[e.statut]}</span>}
              {e.echelle_difficulte && <span title="Échelle de difficulté">{e.echelle_difficulte}</span>}
              <span>💬 {e.nb_retours} retour{e.nb_retours === 1 ? '' : 's'}</span>
              {e.updated_at && <span>MAJ {formatRelatif(e.updated_at)}</span>}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">FRA — Fiches relais d'atelier</span>
          <span className="page-meta">
            {loading ? '…' : `${nbPubliees} fiche${nbPubliees === 1 ? '' : 's'} publiée${nbPubliees === 1 ? '' : 's'} · ${nbAReviser} à réviser · ${nbManquantes} atelier${nbManquantes === 1 ? '' : 's'} sans fiche`}
          </span>
        </div>
      </div>

      {peutGerer && couverture.length > 0 && (
        <div className="fak-couverture mb-16">
          {couverture.map((c) => (
            <div key={c.nom} className="fak-couverture-ligne">
              <span className="fak-couverture-pole">
                {c.couleur && <span className="fak-couverture-dot" style={{ background: c.couleur }} />}
                {c.nom}
              </span>
              <div className="fak-couverture-barre">
                <i style={{ width: `${c.total ? Math.round((c.publiees / c.total) * 100) : 0}%`, background: c.couleur ?? 'var(--accent)' }} />
              </div>
              <span className="text-xs text-ink-3">{c.publiees}/{c.total} publiée{c.publiees === 1 ? '' : 's'}</span>
            </div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <PoleChips poles={poles} selected={poleId} onSelect={setPoleId} />
        <button type="button" className={`chip ${aSoignerSeuls ? 'active' : ''}`} onClick={() => setASoignerSeuls((v) => !v)}>
          À compléter uniquement
        </button>
        <div className="search-wrapper">
          <input
            className="search-input"
            placeholder="Thème, méthode, atelier…" aria-label="Rechercher un thème, une méthode ou un atelier"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {erreur && <div className="badge badge-err mb-16" style={{ display: 'block' }}>{erreur}</div>}

      {loading ? (
        <div className="spinner" />
      ) : filtrees.length === 0 ? (
        <div className="empty-state">Aucune fiche ne correspond à cette recherche.</div>
      ) : (
        <>
          {mesFichesASoigner.length > 0 && (
            <>
              <div className="fak-section-head">
                Vos fiches à compléter
                <span className="fak-section-head-note">vous êtes titulaire ou éditeur·rice</span>
              </div>
              {mesFichesASoigner.map((e) => (
                <div key={e.atelier_id} className="fak-ma-fiche">
                  <span className={fakBadgeClass(e.statut)}>{LIBELLES_STATUT_FAK[e.statut]}</span>
                  <span className="font-medium">{e.atelier_nom}</span>
                  <span className="text-xs text-ink-3">
                    {[
                      e.lieu,
                      e.prochaine_revue && e.prochaine_revue.slice(0, 10) < aujourdhuiIso ? 'revue dépassée' : null,
                      e.nb_retours_non_integres > 0 ? `${e.nb_retours_non_integres} retour${e.nb_retours_non_integres > 1 ? 's' : ''} non intégré${e.nb_retours_non_integres > 1 ? 's' : ''}` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                  <div className="flex gap-8" style={{ marginLeft: 'auto' }}>
                    {(e.statut === 'a_reviser' || (e.statut === 'publie' && e.prochaine_revue != null && e.prochaine_revue.slice(0, 10) < aujourdhuiIso)) && (
                      <button
                        type="button" className="btn btn-sm"
                        disabled={confirmationEnCours === e.atelier_id}
                        title="Rien à changer : enregistre la revue et reprogramme la suivante"
                        onClick={() => confirmerAJour(e.atelier_id)}
                      >
                        {confirmationEnCours === e.atelier_id ? '…' : "✓ Confirmer qu'elle est à jour"}
                      </button>
                    )}
                    <button
                      type="button" className="btn btn-sm btn-accent"
                      onClick={() => setFicheOuverte(e.atelier_id)}
                    >
                      {e.statut === 'manquant' && !e.contenu ? 'Rédiger la fiche' : 'Voir / mettre à jour'}
                    </button>
                  </div>
                </div>
              ))}
              <div className="fak-section-head mt-16">Explorer les fiches</div>
            </>
          )}
          <div className="fak-cards">
            {explorer.map(carte)}
          </div>
        </>
      )}

      {ficheOuverte != null && (
        <FicheFakOverlay atelierId={ficheOuverte} onClose={() => setFicheOuverte(null)} />
      )}
    </div>
  );
}
